
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import JielongIntroCard from "./JielongIntroCard/index.vue";
import {
  JielongIntroCardDetail,
  JielongIntroCardType,
  JielongGoodsItem,
  CommonTag,
  fetchCommonTags,
  aiGenerateIntro,
  mapGoodsToAiProducts,
} from "@/api/jielong";
import { uuid } from "@/utils/util";

interface AddOption {
  type: JielongIntroCardType;
  label: string;
  icon: string;
}

@Component({
  components: { JielongIntroCard },
})
export default class JielongIntro extends Vue {
  @Prop({ type: Array, default: () => [] }) value!: JielongIntroCardDetail[];
  /** 当前接龙商品（供 AI 五字段入参） */
  @Prop({ type: Array, default: () => [] }) goods!: JielongGoodsItem[];
  /** 是否已选择商品，未选择时禁用 AI 生成按钮 */
  @Prop({ type: Boolean, default: true }) hasGoodsSelected!: boolean;
  @Prop({ type: Boolean, default: false }) readonly!: boolean;

  @Getter companyId!: string;

  cards: JielongIntroCardDetail[] = [];
  commonTags: CommonTag[] = [];

  // 删除确认弹层
  deleteVisible = false;
  deleteIndex = -1;

  // 标记“本次 value 变化是自身 emit 触发”，避免逐字输入时全量 JSON 比较与回环
  private selfEmitting = false;

  // ===== AI 状态：idle 未开始 | loading 进行中 | done 已完成 =====
  aiState: "idle" | "loading" | "done" = "idle";
  private aiAbortController: AbortController | null = null;
  /** 重新生成时递增 */
  private aiVariant = 1;
  /** 同一次生成网络重试用的 requestId */
  private aiRequestId = "";
  /** 是否已成功生成过（取消/失败时决定回到 idle 还是 done） */
  private aiHadSuccess = false;
  /** 进行中请求序号：停止/重启时递增，避免已取消请求回写 */
  private aiRequestSeq = 0;

  readonly addOptions: AddOption[] = [
    { type: "bigImage", label: "大图", icon: "add-big-image.png" },
    { type: "smallImage", label: "小图", icon: "add-small-image.png" },
    { type: "video", label: "视频", icon: "add-video.png" },
    { type: "text", label: "文字", icon: "add-text.png" },
    { type: "tag", label: "标签", icon: "add-tag.png" },
  ];

  get isAiBtnDisabled (): boolean {
    return this.readonly;
  }

  @Watch("value")
  onValueChange (val: JielongIntroCardDetail[]): void {
    // 自身 emit 触发的回写直接跳过，仅同步真正的外部变更
    if (this.selfEmitting) {
      this.selfEmitting = false;
      return;
    }
    this.cards = this.cloneCards(val || []);
    this.syncAiStateFromCards();
  }

  created (): void {
    this.cards = this.cloneCards(this.value || []);
    this.syncAiStateFromCards();
  }

  mounted (): void {
    fetchCommonTags(this.companyId).then(tags => {
      this.commonTags = tags;
    });
  }

  /** 若已有 aiText 卡片，按钮展示为「重新生成」；删除后回到 idle */
  private syncAiStateFromCards (): void {
    const hasAi = this.cards.some((c) => c.type === "aiText");
    if (hasAi) {
      this.aiHadSuccess = true;
      if (this.aiState !== "loading") this.aiState = "done";
    } else if (this.aiState !== "loading") {
      this.aiHadSuccess = false;
      this.aiState = "idle";
    }
  }

  private cloneCards (list: JielongIntroCardDetail[]): JielongIntroCardDetail[] {
    return JSON.parse(JSON.stringify(list));
  }

  /** 按当前列表顺序重写 sortNo（从 1 起） */
  private reindexSortNo (): void {
    this.cards.forEach((card, idx) => {
      card.sortNo = idx + 1;
    });
  }

  private nextSortNo (): number {
    if (!this.cards.length) return 1;
    return Math.max(...this.cards.map(c => c.sortNo || 0)) + 1;
  }

  private emitChange (): void {
    this.reindexSortNo();
    this.selfEmitting = true;
    this.$emit("input", JSON.parse(JSON.stringify(this.cards)));
  }

  /**
   * 将 AI 结果落到介绍区：写入/更新唯一的 aiText 卡片
   * 接口可能返回 text 或 aiText，统一落库为 aiText
   */
  private applyAiGeneratedCards (resultCards: JielongIntroCardDetail[]): void {
    const src = (resultCards || []).find(
      (c) => c.type === "aiText" || c.type === "text"
    );
    const textValue =
      src && src.value != null && String(src.value).trim() !== ""
        ? String(src.value)
        : null;

    if (textValue == null) {
      for (let i = this.cards.length - 1; i >= 0; i--) {
        if (this.cards[i].type === "aiText") {
          this.cards.splice(i, 1);
        }
      }
      return;
    }

    const idx = this.cards.findIndex((c) => c.type === "aiText");
    if (idx >= 0) {
      this.$set(this.cards, idx, {
        ...this.cards[idx],
        type: "aiText",
        value: textValue,
      });
      return;
    }

    this.cards.unshift({
      sortNo: 1,
      type: "aiText",
      value: textValue,
    });
  }

  /** 查找可滚动父级（优先 .new-jielong-content） */
  private findScrollContainer (from: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = from.parentElement;
    while (el) {
      if (el.classList.contains("new-jielong-content")) return el;
      const style = window.getComputedStyle(el);
      const oy = style.overflowY;
      if (oy === "auto" || oy === "scroll") return el;
      el = el.parentElement;
    }
    return null;
  }

  /** 若 aiText 卡顶部在可视区底部之下，则滚动到该卡 */
  private scrollToAiTextCardIfBelow (): void {
    const root = this.$el as HTMLElement;
    const cardEl = root.querySelector(
      '[data-card-type="aiText"]'
    ) as HTMLElement | null;
    if (!cardEl) return;

    const container = this.findScrollContainer(cardEl);
    if (!container) return;

    const cRect = container.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();
    if (cardRect.top <= cRect.bottom - 8) return;

    const targetTop =
      container.scrollTop + (cardRect.top - cRect.top) - 12;
    container.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });
  }

  // ===== AI（单次请求整包返回，无轮询） =====
  startAiCreate (): void {
    if (this.readonly) return;
    if (!this.hasGoodsSelected || !this.goods.length) {
      this.$toast("请先选择商品，再进行生成");
      return;
    }

    const products = mapGoodsToAiProducts(this.goods);
    const invalid = products.find(
      (p) => !p.saleTitle || !p.specification || Number.isNaN(p.price)
    );
    if (invalid) {
      this.$toast("请完善商品的销售标题、接龙规格和接龙价后再生成");
      return;
    }

    // 重新生成且卡片在下方时，先滚到卡片位置
    if (this.cards.some((c) => c.type === "aiText")) {
      this.$nextTick(() => this.scrollToAiTextCardIfBelow());
    }

    // 重新生成：新 requestId + variant+1；首次 / 取消后再点：新 requestId，variant 重置为 1
    if (this.aiState === "done") {
      this.aiVariant += 1;
    } else {
      this.aiVariant = 1;
    }
    this.aiRequestId = uuid();

    if (this.aiAbortController) {
      this.aiAbortController.abort();
    }
    this.aiAbortController = new AbortController();
    this.aiState = "loading";
    const requestSeq = ++this.aiRequestSeq;

    aiGenerateIntro(
      {
        requestId: this.aiRequestId,
        source: "nanguatou",
        products,
        copyOptions: {
          length: "medium",
          tone: "friendly",
          emoji: "moderate",
          variant: this.aiVariant,
        },
      },
      this.aiAbortController.signal
    )
      .then((result) => {
        if (requestSeq !== this.aiRequestSeq) return;
        this.aiAbortController = null;
        this.applyAiGeneratedCards(result.cards || []);
        // 先退出 loading，再 sync；否则 syncAiStateFromCards 会因 loading 守卫保持「停止生成」
        this.aiState = "done";
        this.emitChange();
        this.syncAiStateFromCards();
        if (result.warnings && result.warnings.length) {
          this.$toast(result.warnings[0]);
        }
      })
      .catch((err: Error) => {
        if (err.name === "AbortError" || requestSeq !== this.aiRequestSeq) return;
        this.aiAbortController = null;
        this.aiState = this.aiHadSuccess ? "done" : "idle";
        this.$toast(err.message || "生成失败，请稍后重试");
      });
  }

  stopAiCreate (): void {
    this.aiRequestSeq += 1;
    if (this.aiAbortController) {
      this.aiAbortController.abort();
      this.aiAbortController = null;
    }
    this.aiState = this.aiHadSuccess ? "done" : "idle";
  }

  // tag 类型只允许添加一张
  isOptionDisabled (type: JielongIntroCardType): boolean {
    if (this.aiState === 'loading') return true;
    if (type === 'tag') return this.cards.some(c => c.type === 'tag');
    return false;
  }

  addCard (type: JielongIntroCardType): void {
    if (this.readonly) return;
    if (this.isOptionDisabled(type)) return;
    let initialValue: JielongIntroCardDetail["value"] = null;
    switch (type) {
      case "smallImage": initialValue = []; break;
      case "text":       initialValue = ""; break;
      case "tag":        initialValue = []; break;
      default:           initialValue = null; break;
    }
    this.cards.push({ sortNo: this.nextSortNo(), type, value: initialValue });
    this.emitChange();
  }

  /** 点击空态：自动添加文字卡片并聚焦输入 */
  onEmptyClick (): void {
    if (this.readonly) return;
    // 同步占位聚焦，保证移动端键盘能随用户手势弹出
    const temp = document.createElement("textarea");
    temp.setAttribute("readonly", "readonly");
    temp.style.cssText = "position:fixed;left:0;top:0;opacity:0;height:0;font-size:16px;";
    document.body.appendChild(temp);
    temp.focus();

    this.addCard("text");
    this.$nextTick(() => {
      const refs = this.$refs.introCards as any;
      const list = Array.isArray(refs) ? refs : refs ? [refs] : [];
      const last = list[list.length - 1];
      if (last && typeof last.focusTextarea === "function") {
        last.focusTextarea();
      }
      if (temp.parentNode) temp.parentNode.removeChild(temp);
    });
  }

  // ===== 卡片内容回写 =====
  onCardInput (idx: number, newCard: JielongIntroCardDetail): void {
    if (this.readonly) return;
    this.$set(this.cards, idx, newCard);
    this.emitChange();
  }

  // ===== 排序 =====
  moveUp (i: number): void {
    if (this.readonly) return;
    if (i <= 0) return;
    const tmp = this.cards[i - 1];
    this.$set(this.cards, i - 1, this.cards[i]);
    this.$set(this.cards, i, tmp);
    this.emitChange();
  }

  moveDown (i: number): void {
    if (this.readonly) return;
    if (i >= this.cards.length - 1) return;
    const tmp = this.cards[i + 1];
    this.$set(this.cards, i + 1, this.cards[i]);
    this.$set(this.cards, i, tmp);
    this.emitChange();
  }

  moveTop (i: number): void {
    if (this.readonly) return;
    if (i <= 0) return;
    const [item] = this.cards.splice(i, 1);
    this.cards.unshift(item);
    this.emitChange();
  }

  // ===== 删除（二次确认） =====
  askDelete (i: number): void {
    if (this.readonly) return;
    this.deleteIndex = i;
    this.deleteVisible = true;
  }

  cancelDelete (): void {
    this.deleteVisible = false;
    this.deleteIndex = -1;
  }

  confirmDelete (): void {
    if (this.readonly) return;
    if (this.deleteIndex >= 0 && this.deleteIndex < this.cards.length) {
      this.cards.splice(this.deleteIndex, 1);
      this.emitChange();
      this.syncAiStateFromCards();
    }
    this.cancelDelete();
  }

  // ===== 常用标签本地同步（API 由子组件调用，此处只更新列表） =====
  onAddCommonTag (tags: CommonTag[]): void {
    this.commonTags = tags || [];
  }

  onDeleteCommonTag (label: string): void {
    this.commonTags = this.commonTags.filter(t => t.label !== label);
    // 级联清理：移除所有标签卡片中已选中的该标签
    let changed = false;
    this.cards.forEach((card, idx) => {
      if (card.type === "tag" && Array.isArray(card.value) && (card.value as string[]).indexOf(label) > -1) {
        const nextTags = (card.value as string[]).filter(t => t !== label);
        this.$set(this.cards, idx, { ...card, value: nextTags });
        changed = true;
      }
    });
    if (changed) {
      this.emitChange();
    }
  }
}
